import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { hasPermissionFromToken } from '../../utils/rolePermissions';

interface TeamLeader {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface Team {
  groupId: string;
  groupName: string;
  description: string;
  businessId: string;
  createdAt: string;
  teamLeaderId: string;
  teamLeaderFirstName: string;
  teamLeaderLastName: string;
  teamLeaderEmail: string;
  teamLeaders?: TeamLeader[];
  memberCount: number;
}

interface User {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
}

interface TeamMember {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  addedAt: string;
  isTeamLeader?: boolean;
}

const getUserInfoFromToken = (): { userId: string | null; role: string | null } => {
  const token = localStorage.getItem('token');
  if (!token) return { userId: null, role: null };
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const decoded = JSON.parse(jsonPayload);
    // The JWT stores user ID as 'id', not 'userId'
    return { userId: decoded.id || null, role: decoded.role || null };
  } catch (error) {
    console.error('Error decoding token:', error);
    return { userId: null, role: null };
  }
};

const TeamManagement: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openMemberDialog, setOpenMemberDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [formData, setFormData] = useState({
    groupName: '',
    description: '',
  });
  const [activeTab, setActiveTab] = useState(0);
  const [selectedTeamLeaders, setSelectedTeamLeaders] = useState<string[]>([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [teamLeaderRoleChoice, setTeamLeaderRoleChoice] = useState<'TEAM_LEADER' | 'ADMIN'>('TEAM_LEADER');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isTeamLeader, setIsTeamLeader] = useState(false);
  const canManageUsers = hasPermissionFromToken('manage_users');
  const canAssignTeamLeaderRoles = hasPermissionFromToken('manage_users') || hasPermissionFromToken('manage_roles');
  
  const leaderEligibleUsers = users.filter(
    (user) => user.role !== 'Owner' && user.role !== 'Super Admin' && user.isActive
  );

  const getLeaderSelectOptions = () => {
    const eligibleIds = new Set(leaderEligibleUsers.map((user) => user.userId));
    const currentSelections = users.filter(
      (user) => selectedTeamLeaders.includes(user.userId) && !eligibleIds.has(user.userId)
    );
    return [...leaderEligibleUsers, ...currentSelections].sort((a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
    );
  };

  // Get current user info on mount
  useEffect(() => {
    const { userId, role } = getUserInfoFromToken();
    setCurrentUserId(userId);
    setIsTeamLeader(role === 'Team Leader');
  }, []);

  const getTeamLeaderIds = (team: Team): string[] => {
    if (team.teamLeaders?.length) {
      return team.teamLeaders.map((leader) => leader.userId);
    }
    return team.teamLeaderId ? [team.teamLeaderId] : [];
  };

  const formatTeamLeadersDisplay = (team: Team): string => {
    if (team.teamLeaders?.length) {
      return team.teamLeaders
        .map((leader) => `${leader.firstName} ${leader.lastName}`)
        .join(', ');
    }
    if (team.teamLeaderFirstName && team.teamLeaderLastName) {
      return `${team.teamLeaderFirstName} ${team.teamLeaderLastName}`;
    }
    return 'Not assigned';
  };

  const resetTeamLeaderSelection = () => {
    setSelectedTeamLeaders([]);
    setTeamLeaderRoleChoice('TEAM_LEADER');
  };

  const presetTeamLeaderSelection = (team: Team) => {
    const leaderIds = getTeamLeaderIds(team);
    if (!leaderIds.length) {
      resetTeamLeaderSelection();
      return;
    }
    setSelectedTeamLeaders(leaderIds);
    const primaryLeader = users.find((user) => user.userId === leaderIds[0]);
    if (primaryLeader?.role === 'Admin') {
      setTeamLeaderRoleChoice('ADMIN');
    } else {
      setTeamLeaderRoleChoice('TEAM_LEADER');
    }
  };

  const handleTeamLeaderSelectionChange = (value: string[]) => {
    if (value.length === 0) {
      setError('At least one team leader is required');
      return;
    }
    setSelectedTeamLeaders(value);
    if (value.length !== 1) {
      setTeamLeaderRoleChoice('TEAM_LEADER');
    }
    setError(null);
  };

  const removeTeamLeader = (userId: string) => {
    if (selectedTeamLeaders.length <= 1) {
      setError('Each team must have at least one team leader');
      return;
    }
    handleTeamLeaderSelectionChange(selectedTeamLeaders.filter((id) => id !== userId));
  };

  const openEditTeamDialog = (team: Team) => {
    setEditingTeam(team);
    setFormData({
      groupName: team.groupName,
      description: team.description || '',
    });
    presetTeamLeaderSelection(team);
    setError(null);
    setEditDialogOpen(true);
  };

  // Check if current user can edit a specific team
  const canEditTeam = (team: Team): boolean => {
    if (!isTeamLeader) return true;

    const currentUserIdStr = String(currentUserId);
    return getTeamLeaderIds(team).some((leaderId) => String(leaderId) === currentUserIdStr);
  };

  useEffect(() => {
    fetchTeams();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamMembers(selectedTeam.groupId);
    }
  }, [selectedTeam]);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTeams(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching teams:', error);
      setError('Failed to fetch teams');
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTeamMembers = async (teamId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/teams/${teamId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleOpenMemberDialog = (team: Team) => {
    setSelectedTeam(team);
    setOpenMemberDialog(true);
  };

  const handleCloseMemberDialog = () => {
    setOpenMemberDialog(false);
    setSelectedTeam(null);
    setSelectedUserId('');
  };

  const handleDelete = async (teamId: string) => {
    const team = teams.find((item) => item.groupId === teamId);
    const teamLabel = team?.groupName || 'this team';
    if (!window.confirm(`Delete "${teamLabel}"? This will remove all team members and leaders.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      await axios.delete(`${API_BASE_URL}/teams/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (selectedTeam?.groupId === teamId) {
        setSelectedTeam(null);
      }
      setTeams(teams.filter((item) => item.groupId !== teamId));
      setError(null);
    } catch (error) {
      console.error('Error deleting team:', error);
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response.data.message
          : 'Failed to delete team';
      setError(message);
    }
  };

  const handleAddMember = async () => {
    if (!selectedTeam || !selectedUserId) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/teams/${selectedTeam.groupId}/members`,
        { userId: selectedUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTeamMembers(selectedTeam.groupId);
      handleCloseMemberDialog();
    } catch (error) {
      console.error('Error adding member:', error);
      setError('Failed to add member');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedTeam) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/teams/${selectedTeam.groupId}/members/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTeamMembers(selectedTeam.groupId);
    } catch (error) {
      console.error('Error removing member:', error);
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response.data.message
          : 'Failed to remove member';
      setError(message);
    }
  };

  const handleCreateTeam = async () => {
    try {
      if (selectedTeamLeaders.length === 0) {
        setError('Please select at least one team leader');
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/teams`,
        {
          groupName: newTeamName,
          description: newTeamDescription,
          teamLeaderIds: selectedTeamLeaders,
          teamLeaderRole: teamLeaderRoleChoice
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      setTeams([...teams, response.data]);

      setNewTeamName('');
      setNewTeamDescription('');
      resetTeamLeaderSelection();
      setCreateDialogOpen(false);
      setError(null);
      fetchUsers();
    } catch (error) {
      console.error('Error creating team:', error);
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response.data.message
          : 'Failed to create team';
      setError(message);
    }
  };

  const handleUpdateTeam = async () => {
    try {
      if (selectedTeamLeaders.length === 0) {
        setError('Please select at least one team leader');
        return;
      }

      if (!editingTeam) {
        setError('No team selected for editing');
        return;
      }

      const response = await axios.put(
        `${API_BASE_URL}/teams/${editingTeam.groupId}`,
        {
          groupName: formData.groupName,
          description: formData.description,
          teamLeaderIds: selectedTeamLeaders,
          teamLeaderRole: teamLeaderRoleChoice
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      const updatedTeam = response.data;
      setTeams(teams.map((team) =>
        team.groupId === editingTeam.groupId ? updatedTeam : team
      ));

      if (selectedTeam?.groupId === editingTeam.groupId) {
        setSelectedTeam(updatedTeam);
        fetchTeamMembers(updatedTeam.groupId);
      }

      setEditDialogOpen(false);
      setEditingTeam(null);
      resetTeamLeaderSelection();
      setError(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating team:', error);
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response.data.message
          : 'Failed to update team';
      setError(message);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const teamLeaderSelectMenuProps = {
    sx: { zIndex: 1400 },
    PaperProps: {
      sx: { maxHeight: 280 },
    },
    anchorOrigin: {
      vertical: 'bottom' as const,
      horizontal: 'left' as const,
    },
    transformOrigin: {
      vertical: 'top' as const,
      horizontal: 'left' as const,
    },
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    resetTeamLeaderSelection();
    setError(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingTeam(null);
    resetTeamLeaderSelection();
    setError(null);
  };

  const renderTeamLeaderFields = () => (
    <>
      <FormControl fullWidth margin="dense" sx={{ mt: 1 }}>
        <InputLabel id="team-leaders-label">Team Leaders</InputLabel>
        <Select
          labelId="team-leaders-label"
          multiple
          value={selectedTeamLeaders}
          onChange={(e) =>
            handleTeamLeaderSelectionChange(e.target.value as string[])
          }
          input={<OutlinedInput label="Team Leaders" />}
          MenuProps={teamLeaderSelectMenuProps}
          renderValue={(selected) =>
            selected.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Select team leaders…
              </Typography>
            ) : (
              selected
                .map((userId) => {
                  const user = users.find((u) => u.userId === userId);
                  return user ? `${user.firstName} ${user.lastName}` : userId;
                })
                .join(', ')
            )
          }
        >
          {getLeaderSelectOptions().length > 0 ? (
            getLeaderSelectOptions().map((user) => (
              <MenuItem key={user.userId} value={user.userId}>
                <Checkbox checked={selectedTeamLeaders.includes(user.userId)} />
                <ListItemText
                  primary={`${user.firstName} ${user.lastName}`}
                  secondary={`${user.email} - ${user.role}`}
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem value="" disabled>
              No eligible users available
            </MenuItem>
          )}
        </Select>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Add or remove leaders using the list or chips below. At least one leader is required.
        </Typography>
      </FormControl>
      {selectedTeamLeaders.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {selectedTeamLeaders.map((userId) => {
            const user = users.find((u) => u.userId === userId);
            const teamLeader = editingTeam?.teamLeaders?.find((leader) => leader.userId === userId);
            const displayName = user
              ? `${user.firstName} ${user.lastName}`
              : teamLeader
                ? `${teamLeader.firstName} ${teamLeader.lastName}`
                : userId;
            return (
              <Chip
                key={userId}
                size="small"
                color="primary"
                variant="outlined"
                label={displayName}
                onDelete={
                  selectedTeamLeaders.length > 1
                    ? () => removeTeamLeader(userId)
                    : undefined
                }
              />
            );
          })}
        </Box>
      )}
      {selectedTeamLeaders.length === 1 && (
        <Box sx={{ mt: 2 }}>
          <FormLabel>Team Leader Role</FormLabel>
          <Tooltip title={!canAssignTeamLeaderRoles ? "You do not have permission to change role assignments" : ""}>
            <RadioGroup
              row
              value={teamLeaderRoleChoice}
              onChange={(e) =>
                setTeamLeaderRoleChoice(e.target.value as 'TEAM_LEADER' | 'ADMIN')
              }
            >
              <FormControlLabel
                value="TEAM_LEADER"
                control={<Radio disabled={!canAssignTeamLeaderRoles} />}
                label="Team Leader (default)"
                disabled={!canAssignTeamLeaderRoles}
                sx={{ opacity: !canAssignTeamLeaderRoles ? 0.5 : 1 }}
              />
              <FormControlLabel
                value="ADMIN"
                control={<Radio disabled={!canAssignTeamLeaderRoles} />}
                label="Admin (override)"
                disabled={!canAssignTeamLeaderRoles}
                sx={{ opacity: !canAssignTeamLeaderRoles ? 0.5 : 1 }}
              />
            </RadioGroup>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            Team Leaders cannot create users or manage templates unless those
            permissions are enabled for the Team Leader role under Settings → Roles.
          </Typography>
        </Box>
      )}
    </>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {selectedTeam ? (
        <Box>
          <Button
            variant="text"
            onClick={() => setSelectedTeam(null)}
            sx={{ mb: 2 }}
          >
            ← Back to teams
          </Button>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                {selectedTeam.groupName}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {selectedTeam.description}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Team Leaders:
                </Typography>
                {selectedTeam.teamLeaders?.length ? (
                  selectedTeam.teamLeaders.map((leader) => (
                    <Typography key={leader.userId} variant="body1">
                      {leader.firstName} {leader.lastName}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({leader.email})
                      </Typography>
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body1">
                    {formatTeamLeadersDisplay(selectedTeam)}
                    {selectedTeam.teamLeaderEmail && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({selectedTeam.teamLeaderEmail})
                      </Typography>
                    )}
                  </Typography>
                )}
              </Box>
            </Box>
            <Tooltip title={selectedTeam && !canEditTeam(selectedTeam) ? "You can only manage your own team" : ""}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PersonAddIcon />}
                  onClick={() => {
                    if (selectedTeam && !canEditTeam(selectedTeam)) return;
                    handleOpenMemberDialog(selectedTeam);
                  }}
                  disabled={selectedTeam ? !canEditTeam(selectedTeam) : false}
                  sx={{ 
                    opacity: selectedTeam && !canEditTeam(selectedTeam) ? 0.5 : 1,
                    cursor: selectedTeam && !canEditTeam(selectedTeam) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Add Member
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab label="Members" />
            <Tab label="Settings" />
          </Tabs>

          {activeTab === 0 && (
            <Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Added At</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.userId}>
                        <TableCell>{`${member.firstName} ${member.lastName}`}</TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label={member.role}
                              color={member.role === 'Admin' ? 'primary' : 'default'}
                              size="small"
                            />
                            {member.isTeamLeader && (
                              <Chip label="Team Leader" color="secondary" size="small" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{new Date(member.addedAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              member.isTeamLeader
                                ? 'Remove this user as team leader in Settings before removing them from the team'
                                : selectedTeam && !canEditTeam(selectedTeam)
                                  ? 'You can only manage your own team'
                                  : 'Remove from team'
                            }
                          >
                            <span>
                              <IconButton
                                color="error"
                                onClick={() => {
                                  if (selectedTeam && !canEditTeam(selectedTeam)) return;
                                  if (member.isTeamLeader) return;
                                  handleRemoveMember(member.userId);
                                }}
                                disabled={
                                  member.isTeamLeader ||
                                  (selectedTeam ? !canEditTeam(selectedTeam) : false)
                                }
                                sx={{
                                  opacity:
                                    member.isTeamLeader ||
                                    (selectedTeam && !canEditTeam(selectedTeam))
                                      ? 0.5
                                      : 1,
                                  cursor:
                                    member.isTeamLeader ||
                                    (selectedTeam && !canEditTeam(selectedTeam))
                                      ? 'not-allowed'
                                      : 'pointer',
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {members.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body1" color="text.secondary">
                            No members in this team yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
              <Typography variant="body2" color="text.secondary">
                Update team details, add or remove team leaders, or delete the team entirely.
              </Typography>
              <Tooltip title={selectedTeam && !canEditTeam(selectedTeam) ? 'You can only edit your own team' : ''}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      if (selectedTeam && !canEditTeam(selectedTeam)) return;
                      openEditTeamDialog(selectedTeam);
                    }}
                    disabled={selectedTeam ? !canEditTeam(selectedTeam) : false}
                    sx={{
                      opacity: selectedTeam && !canEditTeam(selectedTeam) ? 0.5 : 1,
                      cursor: selectedTeam && !canEditTeam(selectedTeam) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Edit Team
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={selectedTeam && !canEditTeam(selectedTeam) ? 'You can only delete your own team' : ''}>
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      if (selectedTeam && !canEditTeam(selectedTeam)) return;
                      handleDelete(selectedTeam.groupId);
                    }}
                    disabled={selectedTeam ? !canEditTeam(selectedTeam) : false}
                    sx={{
                      opacity: selectedTeam && !canEditTeam(selectedTeam) ? 0.5 : 1,
                      cursor: selectedTeam && !canEditTeam(selectedTeam) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Delete Team
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h4" gutterBottom>
              Team Management
            </Typography>
            <Tooltip title={!canManageUsers ? "You do not have permission to create new teams" : ""}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    if (!canManageUsers) return;
                    setNewTeamName('');
                    setNewTeamDescription('');
                    resetTeamLeaderSelection();
                    setCreateDialogOpen(true);
                  }}
                  disabled={!canManageUsers}
                  sx={{ 
                    opacity: !canManageUsers ? 0.5 : 1,
                    cursor: !canManageUsers ? 'not-allowed' : 'pointer'
                  }}
                >
                  Create New Team
                </Button>
              </span>
            </Tooltip>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {teams.map((team) => (
              <Grid item xs={12} sm={6} md={4} key={team.groupId}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 6,
                    },
                  }}
                  onClick={() => setSelectedTeam(team)}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <GroupIcon sx={{ mr: 1 }} color="primary" />
                      <Typography variant="h6" component="div">
                        {team.groupName}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {team.description || 'No description'}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Team Leaders:
                      </Typography>
                      <Typography variant="body2">
                        {formatTeamLeadersDisplay(team)}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Created: {new Date(team.createdAt).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                    <Tooltip title={!canEditTeam(team) ? "You can only manage your own team" : "Manage team members"}>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            if (!canEditTeam(team)) return;
                            e.stopPropagation();
                            setSelectedTeam(team);
                            setActiveTab(0); // Set to Members tab
                          }}
                          disabled={!canEditTeam(team)}
                          sx={{ opacity: !canEditTeam(team) ? 0.5 : 1, cursor: !canEditTeam(team) ? 'not-allowed' : 'pointer' }}
                        >
                          <PersonAddIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={!canEditTeam(team) ? "You can only edit your own team" : "Edit team"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            if (!canEditTeam(team)) return;
                            e.stopPropagation();
                            openEditTeamDialog(team);
                          }}
                          disabled={!canEditTeam(team)}
                          sx={{ opacity: !canEditTeam(team) ? 0.5 : 1, cursor: !canEditTeam(team) ? 'not-allowed' : 'pointer' }}
                        >
                          <EditIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={!canEditTeam(team) ? "You can only delete your own team" : "Delete team"}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            if (!canEditTeam(team)) return;
                            e.stopPropagation();
                            handleDelete(team.groupId);
                          }}
                          disabled={!canEditTeam(team)}
                          sx={{ opacity: !canEditTeam(team) ? 0.5 : 1, cursor: !canEditTeam(team) ? 'not-allowed' : 'pointer' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
            {teams.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No teams created yet
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </>
      )}

      <Dialog open={openMemberDialog} onClose={handleCloseMemberDialog}>
        <DialogTitle>Add Member to Team</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select User</InputLabel>
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              label="Select User"
            >
              {users.filter(user => user.isActive).map((user) => (
                <MenuItem key={user.userId} value={user.userId}>
                  {`${user.firstName} ${user.lastName} (${user.email})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMemberDialog}>Cancel</Button>
          <Button onClick={handleAddMember} color="primary" disabled={!selectedUserId}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          Create New Team
          <IconButton
            aria-label="Close create team dialog"
            onClick={handleCloseCreateDialog}
            edge="end"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Team Name"
            fullWidth
            required
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newTeamDescription}
            onChange={(e) => setNewTeamDescription(e.target.value)}
          />
          {renderTeamLeaderFields()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button
            onClick={handleCreateTeam}
            variant="contained"
            color="primary"
            disabled={!newTeamName.trim() || selectedTeamLeaders.length === 0}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          Edit Team
          <IconButton
            aria-label="Close edit team dialog"
            onClick={handleCloseEditDialog}
            edge="end"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Team Name"
            fullWidth
            required
            value={formData.groupName}
            onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          {renderTeamLeaderFields()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            onClick={handleUpdateTeam}
            variant="contained"
            color="primary"
            disabled={!formData.groupName.trim() || selectedTeamLeaders.length === 0}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamManagement; 